// ════════════════════════════════════════════════════════════════════
// Glass-i — Cutting Planner Utilization Report (/erp/production/cutting-planner/utilization).
//
// One row per cutting plan × thickness: area to be cut, sheets utilized, total
// sheet area, yield area, off-cut area, off-cut pieces and % utilization. Every
// figure is a live server aggregate (GET .../cutting-plans/utilization-report)
// — no mock data. Read-only. Area is shown at exactly 3 dp (CLAUDE.md rule 5A);
// sheets/pieces are counts; utilization is a percentage.
// ════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, DatePicker, Input, InputNumber, Select, Space, Spin, Table, Tag, Tooltip, message
} from 'antd';
import { ReloadOutlined, SearchOutlined, DownloadOutlined, AreaChartOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import useAuth from '../../../../hooks/useAuth';
import { cuttingPlannerApi } from './cuttingPlannerApi';
import { hasPermission, PERM, formatThickness } from './cuttingPlannerHelpers';
import { formatArea } from '../../inventory/offcuts/offcutHelpers';
import './CuttingPlanUtilizationReportPage.css';

const { RangePicker } = DatePicker;
const ISO = 'YYYY-MM-DD';
const PAGE_SIZE_OPTIONS = ['25', '50', '100'];

const NF0 = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
const NFS = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 3 });
function fmtInt(v) { const n = Number(v); return Number.isFinite(n) ? NF0.format(Math.round(n)) : '—'; }
function fmtSheets(v) { const n = Number(v); if (!Number.isFinite(n)) return '—'; return Number.isInteger(n) ? NF0.format(n) : NFS.format(n); }
function fmtPct(v) { return v == null || !Number.isFinite(Number(v)) ? '—' : `${Number(v).toFixed(2)}%`; }
function pctColor(v) {
  if (v == null) return 'default';
  const n = Number(v);
  if (n >= 85) return 'green';
  if (n >= 70) return 'gold';
  return 'red';
}
function fmtDate(v) { if (!v) return '—'; const d = dayjs(v); return d.isValid() ? d.format('DD-MMM-YYYY') : '—'; }
function errMessage(e, fallback) {
  return (e && e.response && e.response.data && e.response.data.error && e.response.data.error.message) || (e && e.message) || fallback;
}

export default function CuttingPlanUtilizationReportPage() {
  const { me } = useAuth();
  const canView = hasPermission(me, PERM.VIEW);

  const [dateRange, setDateRange] = useState(null);
  const [thickness, setThickness] = useState(null);
  const [category, setCategory] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [brand, setBrand] = useState(null);
  const [brandOptions, setBrandOptions] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const filters = useMemo(() => {
    const f = {};
    if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
      f.dateFrom = dateRange[0].format(ISO);
      f.dateTo = dateRange[1].format(ISO);
    }
    if (thickness != null && thickness !== '') f.thickness = thickness;
    if (category) f.category = category;
    if (brand) f.brand = brand;
    if ((search || '').trim()) f.search = search.trim();
    return f;
  }, [dateRange, thickness, category, brand, search]);

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const out = await cuttingPlannerApi.utilizationReport({
        ...filters, limit: pageSize, offset: (page - 1) * pageSize
      });
      setData(out || null);
      // category/brand options are computed unfiltered server-side, so they stay stable.
      if (out && Array.isArray(out.categoryOptions)) setCategoryOptions(out.categoryOptions);
      if (out && Array.isArray(out.brandOptions)) setBrandOptions(out.brandOptions);
    } catch (e) {
      message.error({ content: errMessage(e, 'Failed to load the utilization report.'), key: 'cpu-load' });
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [canView, filters, page, pageSize]);

  useEffect(() => { load(); }, [load]);
  // Reset to page 1 whenever a filter changes.
  useEffect(() => { setPage(1); }, [filters]);

  const rows = (data && data.rows) || [];
  const total = Number((data && data.total) || 0);
  const totals = (data && data.totals) || null;

  const onSearch = () => { setSearch(searchInput); setPage(1); };
  const onReset = () => {
    setDateRange(null); setThickness(null); setCategory(null); setBrand(null); setSearchInput(''); setSearch(''); setPage(1);
  };

  const rowKey = (r) => `${r.planId}:${r.thickness}`;

  const columns = useMemo(() => ([
    { title: 'Cutting Plan', dataIndex: 'planNo', width: 116, fixed: 'left',
      render: (v, r) => (
        <div className="leading-tight">
          <div className="font-semibold text-ink-800">{v || `OPT-${r.optNo}`}</div>
          <div className="text-[10px] text-ink-400">{fmtDate(r.planDate)}</div>
        </div>
      ) },
    { title: 'Thk', dataIndex: 'thickness', width: 68, align: 'center',
      render: (v) => (v == null ? '—' : <Tag className="!m-0">{formatThickness(v)}mm</Tag>) },
    { title: 'Area to cut (m²)', dataIndex: 'areaToCutSqmt', width: 116, align: 'right',
      render: (v) => <span className="tabular-nums">{formatArea(v)}</span> },
    { title: 'Sheets', dataIndex: 'sheetsUtilized', width: 82, align: 'right',
      render: (v) => <span className="tabular-nums">{fmtSheets(v)}</span> },
    { title: 'Sheet area (m²)', dataIndex: 'totalSheetSqmt', width: 116, align: 'right',
      render: (v) => <span className="tabular-nums">{Number(v) > 0 ? formatArea(v) : '—'}</span> },
    { title: 'Yield (m²)', dataIndex: 'yieldSqmt', width: 110, align: 'right',
      render: (v) => <span className="tabular-nums text-emerald-600 font-medium">{formatArea(v)}</span> },
    { title: 'Off-cut (m²)', dataIndex: 'offcutSqmt', width: 112, align: 'right',
      render: (v) => <span className="tabular-nums text-violet-600">{Number(v) > 0 ? formatArea(v) : '—'}</span> },
    { title: 'Off-cut pcs', dataIndex: 'offcutPcs', width: 92, align: 'right',
      render: (v) => <span className="tabular-nums">{v ? fmtInt(v) : '—'}</span> },
    { title: '% Util', dataIndex: 'utilizationPct', width: 92, align: 'center', fixed: 'right',
      render: (v) => (v == null ? <Tooltip title="No sheets issued yet"><span className="text-ink-300">—</span></Tooltip>
        : <Tag color={pctColor(v)} className="!m-0 tabular-nums">{fmtPct(v)}</Tag>) }
  ]), []);

  // Display-only CSV of the CURRENT filter set. The server caps each page at
  // 500 rows but reports whole-set footer totals, so we page through ALL rows
  // (up to a safety ceiling) — otherwise the detail rows wouldn't reconcile
  // with the TOTAL line. If the ceiling is ever hit, we say so explicitly
  // rather than silently truncating.
  const exportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const PAGE = 500;
      const MAX_ROWS = 50000;   // 100 pages — a safety backstop, not an expected limit
      const list = [];
      let footer = null;
      let totalCount = null;
      let offset = 0;
      while (offset < MAX_ROWS) {
        const out = await cuttingPlannerApi.utilizationReport({ ...filters, limit: PAGE, offset });
        const rows = (out && out.rows) || [];
        if (footer == null && out && out.totals) footer = out.totals;
        if (totalCount == null && Number.isFinite(Number(out && out.total))) totalCount = Number(out.total);
        list.push(...rows);
        offset += rows.length;
        if (rows.length < PAGE) break;                          // last page reached
        if (totalCount != null && offset >= totalCount) break;   // gathered everything
      }
      const truncated = totalCount != null && list.length < totalCount;

      const header = ['Cutting Plan', 'Plan Date', 'Thickness (mm)', 'Area to cut (m2)', 'Sheets utilized',
        'Total sheet area (m2)', 'Yield area (m2)', 'Off-cut area (m2)', 'Off-cut pcs', '% Utilization'];
      const esc = (s) => {
        const t = s == null ? '' : String(s);
        return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
      };
      const lines = [header.join(',')];
      for (const r of list) {
        lines.push([
          r.planNo || `OPT-${r.optNo}`, fmtDate(r.planDate), r.thickness ?? '',
          r.areaToCutSqmt, r.sheetsUtilized, r.totalSheetSqmt, r.yieldSqmt,
          r.offcutSqmt, r.offcutPcs, r.utilizationPct == null ? '' : r.utilizationPct
        ].map(esc).join(','));
      }
      if (footer) {
        // TOTAL spans the whole filtered set. Label it so a reader knows the
        // detail rows above it (and whether any were dropped by the ceiling).
        const label = truncated ? `TOTAL (all ${totalCount} rows; ${list.length} exported)` : 'TOTAL';
        lines.push([label, '', '', footer.areaToCutSqmt, footer.sheetsUtilized, footer.totalSheetSqmt,
          footer.yieldSqmt, footer.offcutSqmt, footer.offcutPcs,
          footer.utilizationPct == null ? '' : footer.utilizationPct].map(esc).join(','));
      }
      if (truncated) {
        message.warning(`Exported the first ${list.length} of ${totalCount} rows; the TOTAL line spans all ${totalCount}.`);
      }
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cutting-utilization-${dayjs().format('YYYYMMDD-HHmm')}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      message.error(errMessage(e, 'Could not export the report.'));
    } finally {
      setExporting(false);
    }
  }, [filters]);

  if (!canView) {
    return (
      <div className="p-4">
        <Alert type="warning" showIcon message="No access"
          description="You do not have permission to view the cutting planner (erp.production.cutting_planner.view)." />
      </div>
    );
  }

  const summary = () => {
    if (!totals) return null;
    const cell = (content, align = 'right') => (
      <Table.Summary.Cell align={align} className="!bg-surface-50 !font-semibold">{content}</Table.Summary.Cell>
    );
    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} className="!bg-surface-50 !font-semibold">Totals</Table.Summary.Cell>
          <Table.Summary.Cell index={1} align="center" className="!bg-surface-50" />
          {cell(<span className="tabular-nums">{formatArea(totals.areaToCutSqmt)}</span>)}
          {cell(<span className="tabular-nums">{fmtSheets(totals.sheetsUtilized)}</span>)}
          {cell(<span className="tabular-nums">{Number(totals.totalSheetSqmt) > 0 ? formatArea(totals.totalSheetSqmt) : '—'}</span>)}
          {cell(<span className="tabular-nums text-emerald-600">{formatArea(totals.yieldSqmt)}</span>)}
          {cell(<span className="tabular-nums text-violet-600">{Number(totals.offcutSqmt) > 0 ? formatArea(totals.offcutSqmt) : '—'}</span>)}
          {cell(<span className="tabular-nums">{totals.offcutPcs ? fmtInt(totals.offcutPcs) : '—'}</span>)}
          {cell(totals.utilizationPct == null ? '—' : <Tag color={pctColor(totals.utilizationPct)} className="!m-0 tabular-nums">{fmtPct(totals.utilizationPct)}</Tag>, 'center')}
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  return (
    <div className="p-4 cpu-report">
      <Card
        bordered={false}
        title={(
          <span className="inline-flex items-center gap-2 font-semibold text-ink-800">
            <AreaChartOutlined className="text-brand-600" /> Cutting Planner — Utilization Report
          </span>
        )}
        extra={(
          <Button icon={<DownloadOutlined />} onClick={exportCsv} loading={exporting} disabled={rows.length === 0}>
            Export CSV
          </Button>
        )}
        bodyStyle={{ paddingTop: 12 }}
      >
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <RangePicker size="small" value={dateRange} onChange={(v) => setDateRange(v || null)}
            format="DD-MMM-YYYY" allowClear placeholder={['Plan from', 'Plan to']} style={{ width: 250 }} />
          <InputNumber size="small" placeholder="Thickness (mm)" value={thickness} onChange={(v) => setThickness(v)}
            min={0} step={0.5} style={{ width: 140 }} />
          <Select size="small" allowClear showSearch placeholder="All categories"
            value={category || undefined} onChange={(v) => setCategory(v || null)}
            options={categoryOptions.map((c) => ({ value: c, label: c }))}
            optionFilterProp="label" style={{ width: 160 }}
            notFoundContent={<span style={{ fontSize: 11.5, color: '#94a3b8' }}>No categories</span>} />
          <Select size="small" allowClear showSearch placeholder="All brands"
            value={brand || undefined} onChange={(v) => setBrand(v || null)}
            options={brandOptions.map((b) => ({ value: b, label: b }))}
            optionFilterProp="label" style={{ width: 150 }}
            notFoundContent={<span style={{ fontSize: 11.5, color: '#94a3b8' }}>No brands</span>} />
          <Input size="small" allowClear prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
            placeholder="Plan / OPT no…" value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)} onPressEnter={onSearch}
            onBlur={onSearch} style={{ width: 200 }} />
          <Button size="small" onClick={onReset}>Clear</Button>
          <div className="flex-1" />
          <Tooltip title="Refresh"><Button size="small" icon={<ReloadOutlined />} onClick={load} /></Tooltip>
        </div>

        <Spin spinning={loading}>
          <Table
            size="small"
            rowKey={rowKey}
            columns={columns}
            dataSource={rows}
            scroll={{ x: 'max-content' }}
            sticky
            summary={rows.length ? summary : undefined}
            pagination={{
              current: page, pageSize, total,
              showSizeChanger: true, pageSizeOptions: PAGE_SIZE_OPTIONS,
              size: 'small',
              showTotal: (c, range) => (c === 0 ? 'No rows' : `${range[0]}–${range[1]} of ${c}`),
              onChange: (nextPage, nextSize) => {
                if (nextSize !== pageSize) { setPageSize(nextSize); setPage(1); } else { setPage(nextPage); }
              }
            }}
            locale={{ emptyText: loading ? ' ' : 'No cutting plans match these filters.' }}
          />
        </Spin>

        <div className="mt-2 text-[11px] text-ink-400">
          Yield area = area to be cut (finished pieces). % Utilization = area to be cut ÷ total sheet area.
          Rows without issued sheets show “—”. Off-cut figures come from off-cuts recorded against each plan.
        </div>
      </Card>
    </div>
  );
}
